
import { Union, FSharpException } from "./fable_modules/fable-library-js.5.0.0-rc.2/Types.js";
import { union_type, class_type } from "./fable_modules/fable-library-js.5.0.0-rc.2/Reflection.js";
import { Variant, debugActive } from "./Variant.js";
import { WebStorageStateStore, UserManager } from "oidc-client-ts";
import { redirectUri } from "./AuthRedirect.js";
import { concat } from "./fable_modules/fable-library-js.5.0.0-rc.2/String.js";
import { toArray, ofNullable, filter, map } from "./fable_modules/fable-library-js.5.0.0-rc.2/Option.js";
import { tokenKey } from "./Constants.js";
import { iterate, exists } from "./fable_modules/fable-library-js.5.0.0-rc.2/Seq.js";

export class SessionExpired extends FSharpException {
    constructor(Data0) {
        super();
        this.Data0 = Data0;
    }
}

export function SessionExpired_$reflection() {
    return class_type("Auth.SessionExpired", undefined, SessionExpired, class_type("System.Exception"));
}

export const patternInput$004015 = debugActive ? ["https://app.inventory-one.com/auth/realms/development", "inventory-one-development"] : ((Variant.tag === 0) ? ["https://app.meinwerkzeugkoffer.de/auth/realms/mein-werkzeugkoffer", "mein-werkzeugkoffer-browser"] : ["https://app.inventory-one.com/auth/realms/inventory-one", "inventory-one-browser"]);

export const clientId = patternInput$004015[1];

const authority = patternInput$004015[0];

const renewalMarginInSeconds = 30;

const silentRenewTimeoutInSeconds = 8;

export const userManager = new UserManager({
    authority: authority,
    client_id: clientId,
    redirect_uri: redirectUri(),
    silent_redirect_uri: concat(window.location.origin, ..."/silent-renew.html"),
    silentRequestTimeoutInSeconds: silentRenewTimeoutInSeconds,
    response_mode: "fragment",
    monitorSession: false,
    automaticSilentRenew: false,
    userStore: new WebStorageStateStore({
        store: window.localStorage,
    }),
});

function storedUser() {
    return map((value_2) => JSON.parse(value_2), filter((value_1) => (value_1 !== ""), ofNullable(localStorage.getItem(tokenKey))));
}

export function currentAccessToken() {
    return map((user) => user.access_token, storedUser());
}

function remainingSeconds(user) {
    return map((expiresAt) => (expiresAt - (Date.now() / 1000)), user.expires_at);
}

function needsRenewal(user) {
    const matchValue = remainingSeconds(user);
    if (matchValue == null) {
        return false;
    }
    else {
        return matchValue <= renewalMarginInSeconds;
    }
}

function isExpired(user) {
    return exists((remaining) => (remaining <= 0), toArray(remainingSeconds(user)));
}

function dropRefreshToken() {
    iterate((value_1) => {
        const withoutRefreshToken = Object.assign({}, JSON.parse(value_1), {
            refresh_token: void 0,
        });
        localStorage.setItem(tokenKey, JSON.stringify(withoutRefreshToken));
    }, toArray(ofNullable(localStorage.getItem(tokenKey))));
}

class RenewalOutcome extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Renewed", "Rejected", "Unavailable"];
    }
}

function RenewalOutcome_$reflection() {
    return union_type("Auth.RenewalOutcome", [], RenewalOutcome, () => [[["Item", class_type("Feliz.AuthProvider.IOidcUser")]], [], []]);
}

function isTokenEndpointRejection(error) {
    const matchValue = typeof error.error === 'string' ? error.error : undefined;
    let matchResult;
    if (matchValue == null) {
        matchResult = 0;
    }
    else {
        switch (matchValue) {
            case "server_error":
            case "temporarily_unavailable": {
                matchResult = 0;
                break;
            }
            default:
                matchResult = 1;
        }
    }
    switch (matchResult) {
        case 0:
            return false;
        default:
            return true;
    }
}

function signinSilently() {
    let pr_1;
    const pr = userManager.signinSilent();
    pr_1 = (pr.then((user) => {
        if (user == null) {
            return new RenewalOutcome(1, []);
        }
        else {
            return new RenewalOutcome(0, [user]);
        }
    }));
    return pr_1.then(void 0, ((error) => {
        if (isTokenEndpointRejection(error)) {
            return new RenewalOutcome(1, []);
        }
        else {
            return new RenewalOutcome(2, []);
        }
    }));
}

let pendingRenewal = undefined;

function renewSilently() {
    if (pendingRenewal == null) {
        const hasRefreshToken = exists((user) => (user.refresh_token != null), toArray(storedUser()));
        let renewal;
        let pr_2;
        let pr_1;
        const pr = signinSilently();
        pr_1 = (pr.then((outcome) => {
            let matchResult;
            if (outcome.tag === 1) {
                if (hasRefreshToken) {
                    matchResult = 0;
                }
                else {
                    matchResult = 1;
                }
            }
            else {
                matchResult = 1;
            }
            switch (matchResult) {
                case 0: {
                    dropRefreshToken();
                    return signinSilently();
                }
                default:
                    return Promise.resolve(outcome);
            }
        }));
        pr_2 = (pr_1.then((outcome_1) => {
            pendingRenewal = undefined;
            switch (outcome_1.tag) {
                case 1:
                case 2:
                    return undefined;
                default:
                    return outcome_1.fields[0];
            }
        }));
        renewal = (pr_2.then(void 0, ((error) => {
            pendingRenewal = undefined;
            throw error;
        })));
        pendingRenewal = renewal;
        return renewal;
    }
    else {
        return pendingRenewal;
    }
}

export function renewSession() {
    const matchValue = storedUser();
    if (matchValue == null) {
        return Promise.resolve(false);
    }
    else {
        const pr = renewSilently();
        return pr.then((option) => (option != null));
    }
}

export function ensureValidToken() {
    const matchValue = storedUser();
    if (matchValue != null) {
        if (!needsRenewal(matchValue)) {
            const user_1 = matchValue;
            return Promise.resolve(user_1.access_token);
        }
        else {
            const user_2 = matchValue;
            const pr = renewSilently();
            return pr.then((renewed) => {
                if (renewed == null) {
                    if (!isExpired(user_2)) {
                        return user_2.access_token;
                    }
                    else {
                        throw new SessionExpired("silent session renewal failed");
                    }
                }
                else {
                    const renewed_1 = renewed;
                    return renewed_1.access_token;
                }
            });
        }
    }
    else {
        return Promise.resolve(undefined);
    }
}

userManager.events.addAccessTokenExpiring(() => {
    const pr = renewSilently();
    pr.then();
});

